import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Dialog,
  FormField,
  Pane,
  SelectField,
  TextInput,
  toaster,
} from "evergreen-ui";
import AutocompleteFormField from "./AutocompleteFormField";
import useControlHub from "../hooks/useControlHub";

const VendorFormDialog = ({
  isShown,
  onCancel,
  onCloseComplete,
  onConfirm,
  vendorID,
}) => {
  const { api, company } = useControlHub();
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [term, setTerm] = useState("");
  const [terms, setTerms] = useState();
  const [is1099, setIs1099] = useState();
  const [method, setMethod] = useState("invoice");
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .getVendorsTerms()
      .then((data) => {
        setTerms(data);
      })
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    api
      .getPaymentMethods()
      .then((data) => {
        if (!data.success) {
          throw new Error(
            "Couldn't get payment methods: " + (data.message || "-")
          );
        }
        setMethods(data.answer);
      })
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    if (!vendorID || !terms) {
      return;
    }
    api
      .getVendor({ vendorID })
      .then((data) => {
        setName(data["name"] || "");
        setContact(data["contact_email"] || "");
        setTerm(terms.find((term) => term === data["terms"]));
        setIs1099(data["vendor_1099"]);
        setMethod(data["payment_mode"]);
      })
      .catch(() => {});
  }, [api, terms, vendorID]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleCloseComplete = useCallback(() => {
    onCloseComplete();
  }, [onCloseComplete]);

  const handleCreateConfirm = useCallback(() => {
    setLoading(true);
    api
      .createVendor({
        vendorData: {
          name: name || null,
          contact_email: contact || null,
          terms: term || null,
          vendor_1099: is1099 || null,
          payment_mode: method,
        },
      })
      .then((data) => {
        if (!data.success) {
          throw new Error(
            "Vendor couldn't be created: " + (data.message || "-")
          );
        }
        onConfirm(data.vendor);
      })
      .catch((error) => {
        toaster.danger(error.message);
        setLoading(false);
      });
  }, [api, contact, is1099, method, name, onConfirm, term]);

  const handleReplaceConfirm = useCallback(() => {
    setLoading(true);
    api
      .replaceVendor({
        vendorID,
        vendorData: {
          name: name || null,
          contact_email: contact || null,
          terms: term || null,
          vendor_1099: is1099 || null,
          payment_mode: method,
        },
      })
      .then((data) => {
        if (!data.success) {
          throw new Error(
            "Vendor couldn't be updated: " + (data.message || "-")
          );
        }
        onConfirm(data.vendor);
      })
      .catch((error) => {
        toaster.danger(error.message);
        setLoading(false);
      });
  }, [api, contact, is1099, method, name, onConfirm, term, vendorID]);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleNameChange = useCallback((event) => {
    setName(event.target.value);
  }, []);

  const handleContactEmailChange = useCallback((event) => {
    setContact(event.target.value);
  }, []);

  const handleTermsChange = useCallback((item) => {
    setTerm(item);
  }, []);

  const handle1099Change = useCallback(({ target: { checked } }) => {
    setIs1099(checked);
  }, []);

  const handleMethodChange = useCallback((event) => {
    setMethod(event.target.value);
  }, []);

  const paymentMethodOptions = useMemo(() => {
    const paymentMethodsLabels = {
      company_card: "Company Card",
      invoice: "Invoice",
      plastic_card: "Plastic Card",
      virtual_card: "Virtual Card",
    };
    return methods.map((_method) => (
      <option key={`${_method}`} value={_method}>
        {paymentMethodsLabels[_method] || _method}
      </option>
    ));
  }, [methods]);

  return (
    <Dialog
      isConfirmLoading={loading}
      isShown={isShown}
      confirmLabel={vendorID ? "Update" : "Create"}
      onCancel={handleCancel}
      onCloseComplete={handleCloseComplete}
      onConfirm={vendorID ? handleReplaceConfirm : handleCreateConfirm}
      title={vendorID ? "Edit Vendor" : "Create New Vendor"}
    >
      <form onSubmit={handleSubmit}>
        <Pane
          display="flex"
          flexDirection="column"
          marginX="auto"
          maxWidth="30rem"
        >
          <Pane display="flex" flex="1 1 auto" flexWrap="wrap">
            <FormField
              flex="1 1 auto"
              label="Vendor Name"
              labelFor="name"
              margin={8}
            >
              <TextInput
                id="name"
                onChange={handleNameChange}
                value={name}
                width="100%"
              />
            </FormField>
          </Pane>
          <Pane display="flex">
            <FormField
              flex="1"
              label="Point of Contact Email"
              labelFor="contact_email"
              margin={8}
            >
              <TextInput
                id="contact_email"
                onChange={handleContactEmailChange}
                placeholder="(Optional)"
                type="email"
                value={contact}
                width="100%"
              />
            </FormField>
            <AutocompleteFormField
              flex="1"
              items={terms || []}
              label="Payment Terms"
              margin={8}
              onChange={handleTermsChange}
              placeholder="(Optional)"
              value={term}
            />
          </Pane>
          <Pane margin={8}>
            <Checkbox
              checked={is1099}
              label="1099 Vendor"
              marginY={8}
              onChange={handle1099Change}
            />
          </Pane>
          {company.features.billPayments ? (
            <Pane marginTop={8} marginX={8}>
              <SelectField
                label="Pay By"
                onChange={handleMethodChange}
                placeholder="Select a payment method"
                value={method}
                width="100%"
              >
                {paymentMethodOptions}
              </SelectField>
            </Pane>
          ) : null}
        </Pane>
      </form>
    </Dialog>
  );
};

export default VendorFormDialog;
